import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import { Loading, SearchInput } from '@avantstay/backoffice-core'

import { EmptyMessage } from '@new/__global/components/EmptyMessage'
import { TapeType } from '@new/__global/components/FastTapeChart/__types'
import { storeHolder } from '@new/__global/components/FastTapeChart/stores/TapeChartStore'
import { TemplateContainer } from '@new/__global/components/OmniSearch'
import { hotelOmniSearchStore } from '@new/domains/hotelTapeChart/components/HotelOmniSearch/HotelOmniSearchStore'
import { HotelTapeChartStore } from '@new/domains/hotelTapeChart/stores/HotelTapeChartStore'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  size as middlewareSize,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'

import { BookingsResults } from './components/BookingResults'
import * as S from './HotelOmniSearch.styles'
import { HotelBookingProps } from './HotelOmniSearch.types'

const _HotelOmniSearch = () => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>()
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const { hotelId } = (storeHolder.store as HotelTapeChartStore).filterStore

  const { hotelOmniSearchBookings, isLoadingResults, setSearchHotelId, setSearchTermParams } =
    hotelOmniSearchStore

  const { context, refs, floatingStyles } = useFloating<HTMLElement>({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
    open: isMenuOpen,
    onOpenChange: setIsMenuOpen,
    middleware: [
      offset({
        crossAxis: 20,
      }),
      flip(),
      middlewareSize({
        apply({ elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
          })
        },
        padding: 10,
      }),
    ],
  })

  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  useEffect(() => {
    if (hotelId) {
      setSearchHotelId(hotelId)
    }
  }, [hotelId])

  const handleSearchTerm = (value?: string) => {
    setSearchTermParams(value)
    setSearchTerm(value)
    if (value && value?.length >= 3) {
      setIsMenuOpen(true)
    }
  }

  const handleOnBookingClick = (booking: HotelBookingProps) => {
    const { tapesStore, filterStore } = storeHolder.store as HotelTapeChartStore
    if (filterStore.roomTypeId && filterStore.roomTypeId !== booking.hotelRoomType?.id) {
      filterStore.setRoomTypeId(booking.hotelRoomType?.id)
    }

    tapesStore.select({ ids: booking.id, type: TapeType.Booking })
  }

  return (
    <S.Wrapper ref={refs.setReference} {...getReferenceProps()}>
      <SearchInput
        onChange={handleSearchTerm}
        searchTerm={searchTerm}
        width={230}
        placeholder="Search Booking"
        iconSearchSize={{ width: 14, height: 14 }}
      />
      {isMenuOpen ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <S.DropdownContainer
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div>
                {isLoadingResults ? (
                  <Loading size="6px" />
                ) : (
                  <TemplateContainer
                    title="Bookings"
                    totalCount={hotelOmniSearchBookings?.total ?? 0}
                  >
                    {hotelOmniSearchBookings && hotelOmniSearchBookings.results.length > 0 ? (
                      <BookingsResults
                        bookings={hotelOmniSearchBookings.results}
                        onClick={handleOnBookingClick}
                        searchTeam={searchTerm || ''}
                        totalCount={hotelOmniSearchBookings.total}
                      />
                    ) : (
                      <EmptyMessage>
                        We didn't find anything, try a different search term.
                      </EmptyMessage>
                    )}
                  </TemplateContainer>
                )}
              </div>
            </S.DropdownContainer>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </S.Wrapper>
  )
}

export const HotelOmniSearch = observer(_HotelOmniSearch)
